import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Check, Phone, MapPin, ArrowRight, Menu, X, Building2, Snowflake, Trees, Wrench, Shield, Clock } from 'lucide-react';

const Commercial = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const scrollToContact = () => {
    window.location.href = '/#contact';
  };

  const commercialServices = [
    {
      icon: Snowflake,
      title: "Commercial Snow Removal",
      description: "24/7 snow and ice management for commercial properties, HOAs, and condominium complexes.",
      features: [
        "Parking lot clearing and plowing",
        "Sidewalk and entrance maintenance",
        "Ice control and salting",
        "24/7 emergency response",
        "Seasonal contracts available"
      ],
      badge: "SIMA ASM Certified"
    },
    {
      icon: Trees,
      title: "Commercial Landscaping",
      description: "Professional landscape design, installation, and construction for commercial properties.",
      features: [
        "Landscape design and installation",
        "Hardscaping and paver work",
        "Irrigation system installation",
        "Outdoor lighting",
        "Tree and shrub planting"
      ]
    },
    {
      icon: Wrench,
      title: "Property Maintenance",
      description: "Ongoing grounds maintenance to keep your commercial property looking pristine.",
      features: [
        "Regular lawn care and mowing",
        "Seasonal cleanups",
        "Garden bed maintenance",
        "Pruning and trimming",
        "Property inspections"
      ]
    }
  ];

  const clientTypes = [
    { icon: Building2, name: "Office Buildings", desc: "Corporate campuses and business parks" },
    { icon: Building2, name: "Retail Properties", desc: "Shopping centers and storefronts" },
    { icon: Building2, name: "HOA Communities", desc: "Homeowner associations and planned communities" },
    { icon: Building2, name: "Condominiums", desc: "Multi-unit residential complexes" },
    { icon: Building2, name: "Industrial Properties", desc: "Warehouses and industrial parks" },
    { icon: Building2, name: "Healthcare Facilities", desc: "Medical centers and care homes" }
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Premium Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-slate-950/98 via-emerald-950/95 to-slate-950/98 backdrop-blur-xl border-b border-emerald-500/20 shadow-[0_4px_30px_rgba(16,185,129,0.15)]">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-20 md:h-28">
            <Link to="/" className="flex items-center group -ml-2 md:-ml-4 mt-1 md:mt-2">
              <div className="relative">
                <div className="absolute -inset-2 md:-inset-3 bg-gradient-to-r from-emerald-500/40 via-green-400/30 to-emerald-500/40 rounded-2xl blur-xl opacity-70 group-hover:opacity-100 transition-opacity duration-500"></div>
                <img 
                  src="https://customer-assets-lqy194kg.emergentagent.net/job_90e246be-0711-4e27-8caa-13f1ea345ba5/artifacts/pl6ilas6_TNA%20Logo.png" 
                  alt="True North Alberta" 
                  className="relative h-16 md:h-24 w-auto rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.4),0_0_60px_rgba(16,185,129,0.3)] border-2 border-emerald-400/30"
                  style={{filter: 'drop-shadow(0 0 20px rgba(16,185,129,0.4))'}}
                />
              </div>
            </Link>
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/services" className="relative text-gray-200 hover:text-emerald-400 transition-all duration-300 font-semibold text-sm uppercase tracking-wider group">
                Services
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-emerald-400 to-green-400 group-hover:w-full transition-all duration-300"></span>
              </Link>
              <Link to="/commercial" className="relative text-emerald-400 font-bold text-sm uppercase tracking-wider">
                Commercial
                <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-gradient-to-r from-emerald-400 to-green-400"></span>
              </Link>
              <Link to="/gallery" className="relative text-gray-200 hover:text-emerald-400 transition-all duration-300 font-semibold text-sm uppercase tracking-wider group">
                Gallery
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-emerald-400 to-green-400 group-hover:w-full transition-all duration-300"></span>
              </Link>
              <Link to="/faq" className="relative text-gray-200 hover:text-emerald-400 transition-all duration-300 font-semibold text-sm uppercase tracking-wider group">
                FAQ
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-emerald-400 to-green-400 group-hover:w-full transition-all duration-300"></span>
              </Link>
              <Button 
                onClick={scrollToContact} 
                className="relative overflow-hidden bg-gradient-to-r from-emerald-600 via-green-500 to-emerald-600 hover:from-emerald-500 hover:via-green-400 hover:to-emerald-500 text-white font-bold px-8 py-3 rounded-full shadow-[0_4px_20px_rgba(16,185,129,0.4)] transition-all duration-300 border border-emerald-400/30"
              >
                Get a Quote
              </Button>
            </nav>
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-white hover:text-emerald-400 transition-colors"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-slate-950 border-b border-emerald-500/20 shadow-2xl z-50">
            <nav className="container mx-auto px-6 py-6 flex flex-col gap-1">
              <Link to="/services" onClick={() => setMobileMenuOpen(false)} className="text-gray-200 hover:text-emerald-400 hover:bg-emerald-500/10 transition-all duration-300 font-semibold text-lg py-3 px-4 rounded-lg">Services</Link>
              <Link to="/commercial" onClick={() => setMobileMenuOpen(false)} className="text-emerald-400 font-semibold text-lg py-3 px-4 rounded-lg bg-emerald-500/10">Commercial</Link>
              <Link to="/gallery" onClick={() => setMobileMenuOpen(false)} className="text-gray-200 hover:text-emerald-400 hover:bg-emerald-500/10 transition-all duration-300 font-semibold text-lg py-3 px-4 rounded-lg">Gallery</Link>
              <Link to="/faq" onClick={() => setMobileMenuOpen(false)} className="text-gray-200 hover:text-emerald-400 hover:bg-emerald-500/10 transition-all duration-300 font-semibold text-lg py-3 px-4 rounded-lg">FAQ</Link>
              <Button onClick={() => { scrollToContact(); setMobileMenuOpen(false); }} className="mt-4 bg-gradient-to-r from-emerald-600 via-green-500 to-emerald-600 text-white font-bold px-8 py-3 rounded-full shadow-lg">Get a Quote</Button>
            </nav>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center pt-32 pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/90e246be-0711-4e27-8caa-13f1ea345ba5/images/a94a27168f7ea1349372ea6abd7ea1dfd96b76e4e1e448adfad7bd8cfdfc1e3d.jpeg" 
            alt="Professionally landscaped commercial office campus in Calgary"
            className="w-full h-full object-cover animate-ken-burns"
            data-testid="commercial-hero-image"
          />
        </div>
        <div className="absolute inset-0 bg-slate-950/45"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/85 via-slate-950/30 to-slate-950"></div>
        
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full mb-8">
              <Building2 className="w-4 h-4 text-emerald-400" />
              <span className="text-emerald-400 text-sm font-semibold uppercase tracking-widest">Commercial Services</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-6 leading-tight">
              Calgary's Trusted <span className="bg-gradient-to-r from-emerald-400 to-green-400 bg-clip-text text-transparent">Commercial Property</span> Partner
            </h1>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed max-w-2xl mx-auto">
              Professional landscaping, snow removal, and property maintenance for HOAs, condominiums, retail centers, and commercial complexes throughout Calgary and surrounding areas.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button 
                onClick={scrollToContact}
                className="bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 text-white font-bold px-8 py-4 rounded-full text-lg shadow-lg"
              >
                Request Commercial Quote
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-12 px-4 bg-slate-900 border-y border-slate-800">
        <div className="container mx-auto">
          <div className="flex flex-wrap justify-center items-center gap-8 lg:gap-16">
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8 text-emerald-400" />
              <div>
                <p className="text-white font-bold">Fully Insured</p>
                <p className="text-gray-400 text-sm">Licensed & Bonded</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets-lqy194kg.emergentagent.net/job_90e246be-0711-4e27-8caa-13f1ea345ba5/artifacts/1valnauv_ASM%20Logo.png" 
                alt="SIMA ASM Certified" 
                className="h-12 w-auto bg-white rounded-lg p-1"
              />
              <div>
                <p className="text-white font-bold">SIMA Certified</p>
                <p className="text-gray-400 text-sm">Advanced Snow Manager</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-emerald-400" />
              <div>
                <p className="text-white font-bold">24/7 Service</p>
                <p className="text-gray-400 text-sm">Emergency Response</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Commercial Services */}
      <section className="py-24 px-4 lg:px-8 bg-slate-950">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-black text-white mb-4">
              Commercial <span className="bg-gradient-to-r from-emerald-400 to-green-400 bg-clip-text text-transparent">Solutions</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Comprehensive property services tailored for commercial clients
            </p>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-8">
            {commercialServices.map((service, idx) => (
              <Card key={idx} className="group bg-slate-800/50 backdrop-blur-sm border-2 border-slate-700/50 hover:border-emerald-500/50 rounded-3xl transition-all duration-500 hover:shadow-[0_20px_60px_rgba(16,185,129,0.15)]">
                <CardHeader className="relative">
                  {service.badge && (
                    <div className="absolute top-4 right-4 bg-white rounded-lg p-2">
                      <img 
                        src="https://customer-assets-lqy194kg.emergentagent.net/job_90e246be-0711-4e27-8caa-13f1ea345ba5/artifacts/1valnauv_ASM%20Logo.png" 
                        alt="SIMA ASM Certified" 
                        className="h-6 w-auto"
                      />
                    </div>
                  )}
                  <div className="w-16 h-16 bg-gradient-to-br from-emerald-600 to-green-600 rounded-2xl flex items-center justify-center mb-4">
                    <service.icon className="w-8 h-8 text-white" />
                  </div>
                  <CardTitle className="text-2xl text-white group-hover:text-emerald-400 transition-colors">{service.title}</CardTitle>
                  <CardDescription className="text-gray-400 text-base">{service.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {service.features.map((feature, fidx) => (
                      <li key={fidx} className="flex items-start gap-3 text-gray-300">
                        <div className="w-5 h-5 mt-0.5 rounded-full bg-gradient-to-br from-emerald-500 to-green-500 flex items-center justify-center flex-shrink-0">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Client Types */}
      <section className="py-24 px-4 lg:px-8 bg-gradient-to-b from-slate-900 to-slate-950">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-black text-white mb-4">
              Who We <span className="bg-gradient-to-r from-emerald-400 to-green-400 bg-clip-text text-transparent">Serve</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Trusted by property managers and businesses across Calgary
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {clientTypes.map((client, idx) => (
              <div key={idx} className="flex items-start gap-4 bg-slate-800/30 p-6 rounded-2xl border border-slate-700/50 hover:border-emerald-500/30 transition-all">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-600 to-green-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <client.icon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white mb-1">{client.name}</h3>
                  <p className="text-gray-400">{client.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 lg:px-8 bg-gradient-to-r from-emerald-900/50 to-green-900/50">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl lg:text-5xl font-black text-white mb-6">
            Ready to Partner with True North?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Get a customized quote for your commercial property. We offer flexible seasonal contracts and reliable service you can count on.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              onClick={scrollToContact}
              className="bg-white text-emerald-900 hover:bg-gray-100 font-bold px-10 py-4 rounded-full text-lg shadow-lg"
            >
              Request a Quote
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <a href="tel:5873252799">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 font-bold px-10 py-4 rounded-full text-lg">
                <Phone className="mr-2 w-5 h-5" />
                587-325-2799
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 border-t border-slate-800 py-16 px-4 lg:px-8">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-4 gap-12">
            <div className="md:col-span-2">
              <Link to="/" className="inline-block mb-6">
                <img 
                  src="https://customer-assets-lqy194kg.emergentagent.net/job_90e246be-0711-4e27-8caa-13f1ea345ba5/artifacts/pl6ilas6_TNA%20Logo.png" 
                  alt="True North Alberta" 
                  className="h-20 w-auto rounded-xl"
                />
              </Link>
              <p className="text-gray-400 mb-6 max-w-md">
                Calgary's trusted commercial property services company. Professional landscaping, snow removal, and property maintenance for HOAs, condominiums, and commercial properties. Family owned and locally operated.
              </p>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/services" className="text-gray-400 hover:text-emerald-400 transition-colors">Services</Link></li>
                <li><Link to="/commercial" className="text-gray-400 hover:text-emerald-400 transition-colors">Commercial</Link></li>
                <li><Link to="/gallery" className="text-gray-400 hover:text-emerald-400 transition-colors">Gallery</Link></li>
                <li><Link to="/faq" className="text-gray-400 hover:text-emerald-400 transition-colors">FAQ</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Contact</h4>
              <ul className="space-y-3">
                <li className="flex items-center gap-2 text-gray-400">
                  <Phone className="w-4 h-4 text-emerald-400" />
                  <a href="tel:5873252799" className="hover:text-emerald-400 transition-colors">587-325-2799</a>
                </li>
                <li className="flex items-center gap-2 text-gray-400">
                  <MapPin className="w-4 h-4 text-emerald-400" />
                  Calgary & Surrounding Areas
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-12 pt-8 text-center text-gray-500">
            <p>&copy; {new Date().getFullYear()} True North Alberta Property Services. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Commercial;
